import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import { ArrowLeft, Users, Clock, Calendar, UserPlus, Loader2, Save } from "lucide-react";
import TournamentLeaderboard from "@/components/tournaments/TournamentLeaderboard";
import KnockoutBracket from "@/components/tournaments/KnockoutBracket";
import ShareButton from "@/components/shared/ShareButton";
import type { Tables } from "@/integrations/supabase/types";

type TournamentPlayerRow = Tables<"tournament_players">;
type TournamentMatchResultRow = Tables<"tournament_match_results">;

interface TournamentPlayerWithProfile extends TournamentPlayerRow {
  profile: { full_name: string | null; level: number | null };
}

interface TournamentRoundWithMatches extends Tables<"tournament_rounds"> {
  tournament_match_results: TournamentMatchResultRow[];
}

interface ProfileName {
  id: string;
  full_name: string | null;
}

const statusColors: Record<string, string> = {
  draft: "bg-muted text-muted-foreground", registration: "bg-green-500/15 text-green-700",
  active: "bg-blue-500/15 text-blue-700", completed: "bg-gray-500/15 text-gray-700", cancelled: "bg-red-500/15 text-red-700",
};

export default function TournamentDetail() {
  const { id } = useParams<{ id: string }>();
  const { user, profile: _profile } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t } = useTranslation("activities");

  const formatLabels: Record<string, string> = {
    americano: "Americano", mexicano: "Mexicano", team_americano: "Team Americano", team_mexicano: "Team Mexicano", knockout: t("tournament.format.knockout"), round_robin: "Round Robin",
  };
  const statusLabels: Record<string, string> = {
    draft: t("common:status.draft"), registration: t("tournament.status.registration"), active: t("tournament.status.active"), completed: t("tournament.status.completed"), cancelled: t("tournament.status.cancelled"),
  };

  const { data: tournament, isLoading } = useQuery({
    queryKey: ["tournament", id],
    queryFn: async () => {
      const { data, error } = await supabase.from("tournaments").select("*").eq("id", id!).single();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const { data: players = [] } = useQuery({
    queryKey: ["tournament-players", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tournament_players").select("*").eq("tournament_id", id!)
        .order("total_points", { ascending: false });
      if (error) throw error;
      const userIds = (data ?? []).map((p: TournamentPlayerRow) => p.user_id);
      if (userIds.length === 0) return [];
      const { data: profiles } = await supabase.from("public_profiles" as any).select("id, full_name, level").in("id", userIds);
      const profileMap = new Map((profiles ?? []).map((p: ProfileName & { level: number | null }) => [p.id, p]));
      return (data ?? []).map((p: TournamentPlayerRow) => ({
        ...p,
        profile: profileMap.get(p.user_id) ?? { full_name: t("common:unknown"), level: 1 },
      }));
    },
    enabled: !!id,
  });

  const { data: rounds = [] } = useQuery({
    queryKey: ["tournament-rounds", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tournament_rounds")
        .select("*, tournament_match_results(*)")
        .eq("tournament_id", id!)
        .order("round_number", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!id,
  });

  // Profile names for match display
  const allPlayerIds = [...new Set(rounds.flatMap((r: TournamentRoundWithMatches) =>
    (r.tournament_match_results ?? []).flatMap((m: TournamentMatchResultRow) => [...(m.team1_players ?? []), ...(m.team2_players ?? [])])
  ))];
  const { data: matchProfiles = [] } = useQuery({
    queryKey: ["match-profiles-detail", allPlayerIds.join(",")],
    queryFn: async () => {
      if (allPlayerIds.length === 0) return [];
      const { data } = await supabase.from("public_profiles" as any).select("id, full_name").in("id", allPlayerIds);
      return data ?? [];
    },
    enabled: allPlayerIds.length > 0,
  });
  const nameMap = new Map(matchProfiles.map((p: ProfileName) => [p.id, p.full_name ?? "?"]));
  const getNames = (ids: string[]) => ids.map((id) => nameMap.get(id) ?? "?").join(" & ");

  // Realtime
  useEffect(() => {
    if (!id) return;
    const channel = supabase
      .channel(`tournament-${id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "tournament_players", filter: `tournament_id=eq.${id}` }, () => {
        qc.invalidateQueries({ queryKey: ["tournament-players", id] });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "tournament_match_results" }, () => {
        qc.invalidateQueries({ queryKey: ["tournament-rounds", id] });
        qc.invalidateQueries({ queryKey: ["tournament-players", id] });
        qc.invalidateQueries({ queryKey: ["tournament-leaderboard", id] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [id, qc]);

  const isRegistered = players.some((p: TournamentPlayerWithProfile) => p.user_id === user?.id);

  const registerMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("tournament_players").insert({ tournament_id: id!, user_id: user!.id });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tournament-players", id] });
      toast.success(t("tournament.toast.registered"));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Player result reporting
  const [reportScores, setReportScores] = useState<Record<string, { s1: string; s2: string }>>({});

  const reportResultMutation = useMutation({
    mutationFn: async ({ matchId, score1, score2 }: { matchId: string; score1: number; score2: number }) => {
      const { error } = await supabase
        .from("tournament_match_results")
        .update({ score_team1: score1, score_team2: score2, status: "completed", reported_by: user!.id })
        .eq("id", matchId);
      if (error) throw error;

      // Update player points
      const { data: match } = await supabase
        .from("tournament_match_results")
        .select("team1_players, team2_players")
        .eq("id", matchId)
        .single();
      if (!match) return;

      for (const pid of match.team1_players ?? []) {
        const player = players.find((p: TournamentPlayerWithProfile) => p.user_id === pid);
        if (player) {
          await supabase.from("tournament_players")
            .update({ total_points: (player.total_points ?? 0) + score1 })
            .eq("tournament_id", id!)
            .eq("user_id", pid);
        }
      }
      for (const pid of match.team2_players ?? []) {
        const player = players.find((p: TournamentPlayerWithProfile) => p.user_id === pid);
        if (player) {
          await supabase.from("tournament_players")
            .update({ total_points: (player.total_points ?? 0) + score2 })
            .eq("tournament_id", id!)
            .eq("user_id", pid);
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tournament-rounds", id] });
      qc.invalidateQueries({ queryKey: ["tournament-players", id] });
      qc.invalidateQueries({ queryKey: ["tournament-leaderboard", id] });
      toast.success(t("tournament.toast.resultReported"));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) {
    return <div className="space-y-4 pb-20"><Skeleton className="h-40" /><Skeleton className="h-60" /></div>;
  }
  if (!tournament) {
    return <p className="text-center py-12 text-muted-foreground">{t("tournament.notFound")}</p>;
  }

  const isPlayerInMatch = (match: TournamentMatchResultRow) =>
    user && ((match.team1_players ?? []).includes(user.id) || (match.team2_players ?? []).includes(user.id));

  return (
    <div className="space-y-4 pb-20">
      <Button variant="ghost" size="sm" onClick={() => navigate("/tournaments")} className="gap-1">
        <ArrowLeft className="h-4 w-4" /> {t("common:back")}
      </Button>

      <div className="gradient-hero rounded-2xl p-5 -mx-1">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">{tournament.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={statusColors[tournament.status]}>{statusLabels[tournament.status]}</Badge>
              <Badge variant="outline">{formatLabels[tournament.format]}</Badge>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
          <span className="flex items-center gap-1"><Calendar className="h-4 w-4" /> {format(parseISO(tournament.date), "d MMMM yyyy", { locale: sv })}</span>
          <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {tournament.start_time?.slice(0, 5)}{tournament.end_time ? `–${tournament.end_time.slice(0, 5)}` : ""}</span>
          <span className="flex items-center gap-1"><Users className="h-4 w-4" /> {players.length}/{tournament.max_players}</span>
        </div>
      </div>

      {tournament.status === "registration" && !isRegistered && (
        <Button onClick={() => registerMutation.mutate()} disabled={registerMutation.isPending || players.length >= tournament.max_players} className="w-full gap-1">
          {registerMutation.isPending ? <Loader2 className="animate-spin h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
          {players.length >= tournament.max_players ? t("fullBooked") : (tournament.price_per_player ?? 0) > 0 ? t("tournament.registerWithPrice", { price: tournament.price_per_player }) : t("register")}
        </Button>
      )}

      {isRegistered && tournament.status === "registration" && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg px-4 py-2 text-sm text-center">
          {t("tournament.registeredBanner")}
        </div>
      )}

      <div className="flex justify-end">
        <ShareButton entityType="tournament" entityId={id!} title={tournament.name} subtitle={t("tournament.shareSubtitle", { name: tournament.name })} />
      </div>

      <Tabs defaultValue={tournament.format === "knockout" ? "bracket" : "leaderboard"}>
        <TabsList className="w-full">
          {tournament.format === "knockout" && <TabsTrigger value="bracket" className="flex-1">{t("tournament.tabs.bracket")}</TabsTrigger>}
          <TabsTrigger value="leaderboard" className="flex-1">{t("standings")}</TabsTrigger>
          <TabsTrigger value="rounds" className="flex-1">{t("rounds")}</TabsTrigger>
          <TabsTrigger value="info" className="flex-1">{t("tournament.tabs.info")}</TabsTrigger>
        </TabsList>

        {tournament.format === "knockout" && (
          <TabsContent value="bracket" className="mt-3">
            <KnockoutBracket tournamentId={tournament.id} players={players} />
          </TabsContent>
        )}

        <TabsContent value="leaderboard" className="mt-3">
          <TournamentLeaderboard tournamentId={tournament.id} />
        </TabsContent>

        <TabsContent value="rounds" className="space-y-4 mt-3">
          {rounds.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">{t("tournament.noRoundsYet")}</p>
          ) : (
            [...rounds].reverse().map((round: TournamentRoundWithMatches) => (
              <Card key={round.id}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>{t("tournament.roundN", { n: round.round_number })}</span>
                    <Badge variant="outline" className="text-xs">
                      {round.status === "completed" ? t("tournament.roundStatus.done") : round.status === "active" ? t("tournament.roundStatus.active") : t("tournament.roundStatus.pending")}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(round.tournament_match_results ?? []).map((match: TournamentMatchResultRow, mi: number) => {
                    const canReport = isPlayerInMatch(match) && match.status !== "completed";
                    const scores = reportScores[match.id] ?? { s1: String(match.score_team1 ?? ""), s2: String(match.score_team2 ?? "") };

                    return (
                      <div key={match.id} className="rounded-lg border p-3 space-y-2">
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{t("tournament.matchN", { n: mi + 1 })}</span>
                          <Badge variant={match.status === "completed" ? "default" : "outline"} className="text-[10px]">
                            {match.status === "completed" ? t("tournament.matchStatus.done") : t("tournament.matchStatus.pending")}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
                          <div className="text-sm font-medium text-right">
                            {getNames(match.team1_players ?? [])}
                          </div>
                          <div className="flex items-center gap-1">
                            {canReport ? (
                              <>
                                <Input type="number"
                                  className="w-12 h-8 text-center text-sm p-0"
                                  value={scores.s1}
                                  onChange={(e) => setReportScores((prev) => ({ ...prev, [match.id]: { ...scores, s1: e.target.value } }))}
                                />
                                <span className="text-muted-foreground text-xs">–</span>
                                <Input type="number"
                                  className="w-12 h-8 text-center text-sm p-0"
                                  value={scores.s2}
                                  onChange={(e) => setReportScores((prev) => ({ ...prev, [match.id]: { ...scores, s2: e.target.value } }))}
                                />
                              </>
                            ) : (
                              <>
                                <span className="font-bold text-sm">{match.score_team1 ?? "–"}</span>
                                <span className="text-muted-foreground text-xs">–</span>
                                <span className="font-bold text-sm">{match.score_team2 ?? "–"}</span>
                              </>
                            )}
                          </div>
                          <div className="text-sm font-medium">
                            {getNames(match.team2_players ?? [])}
                          </div>
                        </div>
                        {canReport && scores.s1 !== "" && scores.s2 !== "" && (
                          <Button
                            size="sm"
                            className="w-full gap-1"
                            onClick={() => reportResultMutation.mutate({
                              matchId: match.id,
                              score1: parseInt(scores.s1),
                              score2: parseInt(scores.s2),
                            })}
                            disabled={reportResultMutation.isPending}
                          >
                            <Save className="h-3.5 w-3.5" /> {t("tournament.reportResult")}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="info" className="space-y-4 mt-3">
          {tournament.description && (
            <Card><CardContent className="p-4"><p className="text-sm">{tournament.description}</p></CardContent></Card>
          )}
          {tournament.rules_text && (
            <Card>
              <CardHeader><CardTitle className="text-base">{t("tournament.rules")}</CardTitle></CardHeader>
              <CardContent><p className="text-sm whitespace-pre-wrap">{tournament.rules_text}</p></CardContent>
            </Card>
          )}
          <Card>
            <CardContent className="p-4 space-y-1 text-sm">
              <p><span className="text-muted-foreground">{t("format")}:</span> {formatLabels[tournament.format]}</p>
              <p><span className="text-muted-foreground">{t("formatSettings.pointsPerMatch")}:</span> {tournament.points_per_match}</p>
              <p><span className="text-muted-foreground">{t("tournament.minPlayers")}:</span> {tournament.min_players}</p>
              <p><span className="text-muted-foreground">{t("tournament.maxPlayers")}:</span> {tournament.max_players}</p>
              {(tournament.price_per_player ?? 0) > 0 && (
                <p><span className="text-muted-foreground">{t("price")}:</span> {t("common:currencySuffix", { amount: tournament.price_per_player })}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
